import React, { useEffect, useState, useCallback } from "react";

import {
  FormConfig,
  FormField,
  FormService,
} from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useCreateMeterReader, useGetMeterReaderDetail, useUpdateMeterReader, useVerifyMeterReaderEmail } from "../../hooks";
import { convertKeysToSnakeCase } from "@shared/utils/caseConverter";
import { Button } from "@shared/ui/button";
import { Upload, X, AlertTriangle, ArrowLeft } from "lucide-react";
import { toast } from "@shared/ui/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";
import { useSearchParams } from "react-router-dom";
import { FIELD_CLASSES } from "@shared/utils/constants";

interface FileUploadComponentProps {
  
  file: File | null;
  filePreview: string | null;
  isLoading: boolean;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
}

export const FileUploadComponent: React.FC<FileUploadComponentProps> =
  React.memo(({ file, filePreview, isLoading, onFileChange, onRemoveFile }) => {
    return (
      <div className="w-full bg-[#f0f4ff] p-6 rounded-lg border border-blue-100 mb-6 flex gap-6 items-start">
        
        <div className="h-32 w-32 bg-white rounded-xl flex items-center justify-center border border-gray-200 relative shadow-sm shrink-0">
            {filePreview ? (
                <img
                    src={filePreview}
                    alt="Preview"
                    className="h-full w-full object-cover rounded-xl"
                />
            ) : (
                <div className="text-center">
                    <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2 text-blue-600">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <span className="text-xs text-gray-400 font-medium">No photo</span>
                </div>
            )}
             <div className="absolute -bottom-2 -right-2 bg-blue-600 h-8 w-8 rounded-full flex items-center justify-center border-2 border-white cursor-pointer" onClick={() => !isLoading && document.getElementById("file-upload")?.click()}>
                <Upload className="h-4 w-4 text-white" />
             </div>
        </div>
        
        <div className="flex-1 space-y-4">
             <div>
                <h3 className="text-base font-bold text-gray-900 mb-1">Profile Photo</h3>
                <p className="text-sm text-gray-500">Upload a clear, professional photo for employee identification</p>
             </div>

             <div className="flex items-center gap-2 w-full">
                <div
                  className={`border rounded-md px-4 py-2 bg-white flex items-center justify-between cursor-pointer hover:border-blue-500 transition-colors w-full ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() =>
                    !isLoading && document.getElementById("file-upload")?.click()
                  }
                >
                    <span className="text-sm font-medium text-gray-700 truncate">
                        Choose File
                    </span>
                    <span className="text-sm text-gray-500 ml-2 truncate">
                         {file?.name || "No file chosen"}
                    </span>
                 </div>
             </div>

              <div className="bg-white/50 rounded-lg p-3 grid grid-cols-2 gap-x-8 gap-y-2 text-xs text-gray-500">
                    <div className="flex items-center gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-blue-600"></div>
                        Format: JPG, PNG
                    </div>
                     <div className="flex items-center gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-blue-600"></div>
                        Max size: 5MB
                    </div>
                     <div className="flex items-center gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-blue-600"></div>
                        Square ratio preferred
                    </div>
                     <div className="flex items-center gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-blue-600"></div>
                        Clear face visibility
                    </div>
             </div>
        </div>

         {filePreview && (
            <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFile();
                }}
                className="h-6 w-6 p-0 rounded-full bg-gray-100 hover:bg-gray-200 absolute top-4 right-4"
            >
                <X className="h-3 w-3 text-gray-500" />
            </Button>
         )}

      <input
        id="file-upload"
        type="file"
        className="hidden"
        disabled={isLoading}
        onChange={onFileChange}
        accept="image/png,image/jpeg,image/gif,image/webp"
      />
      </div>
    );
  });

interface EditMeterReaderProps {
  details: any;
  onCancel: () => void;
}

const Edit: React.FC<EditMeterReaderProps> = ({
  details,
  onCancel,
}) => {
    const [searchParams] = useSearchParams();
    const id = searchParams.get("edit");
  const remoteUtilityId = useRemoteUtilityId();
  const { mutate: createMeterReader } = useCreateMeterReader();
  const { mutate: updateReader } = useUpdateMeterReader();
  const { mutate: verifyEmail, isPending: isVerifying } = useVerifyMeterReaderEmail();
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
const { data: readerDetail, isLoading: isReaderDetailLoading } = useGetMeterReaderDetail(remoteUtilityId, id);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (!selectedFile) return;

      const allowedTypes = ["image/png", "image/jpeg", "image/gif", "image/webp"];
      if (!allowedTypes.includes(selectedFile.type)) {
        toast({
          title: "Invalid file type",
          description: "Only PNG, JPG, GIF, and WebP images are allowed.",
          variant: "destructive",
        });
        e.target.value = "";
        return;
      }

      setFile(selectedFile);
      readerForm.setValue("file", selectedFile);
      readerForm.clearErrors("file");

      const reader = new FileReader();
      reader.onload = (evt) => {
        setFilePreview(evt.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    },
    []
  );

  const removeFile = useCallback(() => {
    setFile(null);
    setFilePreview(null);
    readerForm.setValue("file", null);
    const fileInput = document.getElementById(
      "file-upload"
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  }, []);

  const fields: FormField[] = [
    {
      name: "file",
      type: "file",
      label: "",
      fullWidth: true,
      className:"col-span-3",
      classes: {
        container: "w-full mb-0", // Reduced margin as component has its own padding
        label: "hidden",
        file: "w-full",
        error: "text-red-600 text-sm mt-1",
      },
      customRender: () => (
        <FileUploadComponent
          file={file}
          filePreview={filePreview}
          isLoading={isLoading}
          onFileChange={handleFileChange}
          onRemoveFile={removeFile}
        />
      ),
    },
    { name: "employeeId", type: "text", label: "Employee ID", placeholder: 'Unique identifier assigned to the employee',required:true }, 
    { name: "name", type: "text", label: "Name", required: true, placeholder: "Employee's full legal name" },
    { 
        name: "email", 
        type: "email", 
        label: "Email", 
        placeholder: 'Primary email for communication',
        customRender: (field: FormField, form: any) => (
             <div className="flex flex-col gap-1 mb-4">

                <div className="flex gap-2">
                    <input
                        {...form.register(field.name)}
                        type="email"
                        disabled={field.disabled}
                        placeholder={field.placeholder}
                        className="rounded-md border px-3 py-2 border-gray-300 w-full focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                     <Button 
                        type="button" 
                        variant="default" 
                        disabled={isVerifying}
                        onClick={() => {
                            const email = form.getValues("email");
                            if(email) {
                                verifyEmail(email);
                            } else {
                                toast({
                                    variant: "destructive",
                                    title: "Error",
                                    description: "Please enter an email address",
                                });
                            }
                        }}
                    >
                        {isVerifying ? (
                            <span className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></span>
                        ) : (
                            "Verify"
                        )}
                     </Button>
                </div>
            </div>
        )
     },
    {
      name: "contactNumber",
      type: "phone",
      label: "Phone",
      required: true,
      placeholder: "Contact number with country code"
    },
    { name: "department", type: "text", label: "Department", required: true, placeholder: "Organizational department assignment",disabled:true },
    { name: "role", type: "text", label: "Role", required: true, placeholder: "Job title or position in the company",disabled:true   },
    { name: "joiningDate", type: "datepicker", label: "Joining Date", placeholder: "Date when employee joined the company" },
    { name: "imeiNo", type: "text", label: "IMEI Number",placeholder: "Device IMEI for field force tracking" },
    { name: "address", type: "text", label: "Address",  placeholder: "Residential address for communication" },
    
    // Password Management Header
    {
        name: "passwordHeader",
        type: "custom", 
        label: "",
        className:"col-span-3",
        fullWidth: true,
         customRender: () => (
             <div className="w-full mt-6 mb-2 pt-4 border-t border-gray-100">
                 <h3 className="text-base font-semibold text-gray-900">Password Management (Field Force App)</h3>
             </div>
         )
    },

    { name: "currentPassword", type: "password", label: "Current Password", disabled: true, placeholder: "........" },
    { name: "password", type: "password", label: "New Password", placeholder: "Enter new password", minLength: 6 },
    {
      name: "retypePassword",
      type: "password",
      label: "Confirm New Password",
      placeholder: "Retype new password"
    },
  ];

  const readerForm = FormService.createForm({ fields });

  const formConfig: FormConfig = {
    fields,
    shouldUnregister: true,
    classes: FIELD_CLASSES,
  };

  const handleSubmitForm = (data: any) => {
    const { password, retypePassword } = data;

    if (password && password !== retypePassword) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Passwords do not match",
        });
      return;
    }

    setIsLoading(true);
    // Remove currentPassword and passwordHeader before submitting
    const { currentPassword, passwordHeader, ...submissionData } = data;
    
    // Only pass password fields if they are filled out
    if (!submissionData.password) {
      delete submissionData.password;
    }
    if (!submissionData.retypePassword) {
      delete submissionData.retypePassword;
    }
    
    const snakeCasedData = convertKeysToSnakeCase(submissionData);

    const formData = new FormData();
    Object.entries(snakeCasedData).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (key === 'file' && !value) return; 

      if (value instanceof File) {
        formData.append(key, value);
      } else if (
        key === "file" &&
        typeof value === "string" &&
        value.startsWith("http")
      ) {
        // Skip
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          formData.append(`${key}[${index}]`, String(item));
        });
      } else if (typeof value === "object") {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    });

    const onSuccess = () => {
      setIsLoading(false);
      onCancel();
      toast({
          title: "Success",
          description: details?.id ? "Meter Reader updated successfully" : "Meter Reader created successfully",
        });
    };

    const onError = (err: any) => {
        setIsLoading(false);
        const errorMsg = err?.response?.data?.message || err?.message || "Something went wrong";
        toast({
            variant: "destructive",
            title: "Error",
            description: errorMsg,
        });
    };

    if (details?.id) {
      updateReader(
        { id: details.id, payload: formData },
        { onSuccess: () => {
          logEvent("MX Meter Reader Detail Updated", {
            readerId: details?.id
          });
          onSuccess();
        }, onError }
      );
    } else {
      formData.append('remote_utility_id', remoteUtilityId)
      createMeterReader(formData, { onSuccess, onError });
    }
  };

  useEffect(() => {
    // Relying on details prop essentially acting as initial data or active record
    if (readerDetail?.id) {
        // Map any existing password (from details) to currentPassword field
        // And ensure the new password fields are empty
        const populateData = { 
            ...readerDetail, 
            employeeId: readerDetail?.employeeId,
            contactNumber: readerDetail?.contactNumber,
            imeiNo: readerDetail?.imeiNo,
            joiningDate: readerDetail?.joiningDate,
            role: readerDetail?.roleCode?.join(", "),
            department: readerDetail?.extraDataMap?.department?.join(", "),
            currentPassword: readerDetail?.password,
            password: "", 
            retypePassword: "" 
        }; 
        FormService.populateForm(readerForm, populateData, fields);

        if (readerDetail.file) {
          setFilePreview(readerDetail.file);
          setFile(readerDetail.file); 
        }
    } 
  }, [readerDetail]);
 
  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 pt-6 border-t mt-6">
      <button
        type="button"
        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50 font-medium"
        onClick={onCancel}
        disabled={isLoading}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={readerForm.handleSubmit(handleSubmitForm)}
        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
        disabled={isLoading}
      >
         {isLoading && <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>}
        Save Changes
      </button>
    </div>
  );

  return (
    <div className="space-y-4 h-full">
      {/* Header */}
      <div className="flex items-center gap-4 mb-2">
            <Button
                variant="ghost"
                size="icon"
                onClick={onCancel}
                className="h-8 w-8 rounded-full hover:bg-gray-100"
            >
                <ArrowLeft className="h-5 w-5 text-gray-700" />
            </Button>
            <div>
                <h1 className="text-xl font-bold text-gray-900">Edit Employee</h1>
                <p className="text-sm text-gray-500 font-medium">
                    {details?.employeeId || "SE001"} - {details?.name || "John Anderson"}
                </p>
            </div>
      </div>

      <div className="bg-white rounded-lg border shadow-sm">
        {/* Banner */}
        {readerDetail?.roleCode?.length > 1 && (
            <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3 flex items-start gap-3 rounded-t-lg">
                 <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                 <div>
                     <h3 className="text-yellow-800 font-semibold text-sm">Multi-Role User Profile</h3>
                     <p className="text-yellow-700 text-xs mt-0.5">This is a multi-role user with a Field Technician role. Any profile updates will also reflect in Field Force Management.</p>
                 </div>
            </div>
        )}

        <div className="p-6">
            <h2 className="text-lg font-bold mb-4">Profile Details</h2>
            
            <DynamicForm
            form={readerForm}
            config={formConfig}
            fields={fields}
            onSubmit={(data) => handleSubmitForm(data)}
            gridColumns={3}
            renderCustomFooter={renderCustomFooter}
            />
        </div>
      </div>
    </div>
  );
};

export default Edit;
